const getTypedQueryParam = (query, propName, type, defaultValue, transformFn) => {
  const effectiveQueryParam = Object.keys(query).find(k => k.toLowerCase().replace('filter', '').replace(/[_-]/g, '') === propName.toLowerCase())
  let value = query[effectiveQueryParam]
  if (defaultValue !== undefined && value === undefined) {
    return defaultValue
  }

  if (typeof transformFn === 'function') {
    value = transformFn(value)
  }

  const types = Array.isArray(type) ? type.filter(t => t != null) : [type]
  const t = types[0]

  switch (t) {
    case Number:
      return value !== undefined ? Number(value) : value
    case Boolean:
      return value !== undefined && value !== 'false'
    default:
      return value
  }
}

const isSimpleType = t => ['String', 'Number', 'Boolean', 'Function', 'Symbol', 'BigInt'].includes(t)

const getDeclaredType = ctor => {
  // Early return for null to avoid unnecessary computations
  if (ctor === null) {
    return 'null'
  }

  // Avoid using regex for common cases by checking the type directly
  if (typeof ctor === 'function') {
    // Using name property to avoid converting function to string
    return ctor.name || ''
  } else if (typeof ctor === 'object') {
    // Attempting to directly access constructor name if possible
    const name = ctor.constructor && ctor.constructor.name
    return name || ''
  }

  // Fallback for other types (though they're less likely to have meaningful names here)
  return ''
}

const validatePropertyWithFallback = (effectiveName, declaration, value, fallback) => {
  if (!declaration) {
    console.warn(`Missing prop declaration for "${effectiveName}": "${value}". The component will treat this as an attribute`)
    return value
  }

  if (value === undefined) {
    return fallback
  }

  const types = Array.isArray(declaration.type) ? declaration.type : [declaration.type]

  let typeValid = true

  try {
    typeValid = types.findIndex(t => {
      const expectedType = getDeclaredType(t)
      if (isSimpleType(expectedType)) {
        const actualType = typeof value
        return expectedType?.toLowerCase() === actualType
      } else {
        return true
      }
    }) !== -1
  } catch (e) {
    console.warn(`Error while executing type check for prop ${effectiveName}:\n${e.toString()}`)
  }

  let valueValid = true

  try {
    valueValid = typeValid && (declaration.validator?.(value) ?? true)
  } catch (e) {
    console.warn(`Error while calling validator for prop ${effectiveName}:\n${e.toString()}`)
  }

  if (!typeValid) {
    console.warn(`property ${effectiveName} does not have required type: ${types}. Actual type is: ${typeof value}`)
  } else if (!valueValid) {
    console.warn(`property ${effectiveName} has an invalid value: "${value}", The configured fallback value will be used: "${fallback}"`)
  }

  return (typeValid && valueValid) ? value : fallback
}

export const useDsQueryParams = ({ propsDeclaration, applicableProps, dataProvider, propNameMapping = {}, propTransform = {} }) => {
  const effectiveProps = {}
  const availableEntries = Object.entries(applicableProps)
  const mappedEntries = Object.entries(propNameMapping).filter(([, v]) => Object.hasOwn(applicableProps, v)).map(([mappedProp, applicableKey]) => ([mappedProp, applicableProps[applicableKey]]))
  availableEntries.push(...mappedEntries)
  availableEntries.forEach(([propName, defaultValue]) => {
    const effectiveName = propNameMapping[propName] ?? propName
    const typedValue = getTypedQueryParam(dataProvider, propName, propsDeclaration[effectiveName]?.type ?? String, defaultValue ?? undefined, propTransform[effectiveName])
    const effectiveValue = validatePropertyWithFallback(effectiveName, propsDeclaration[effectiveName], typedValue, defaultValue ?? undefined)

    effectiveProps[effectiveName] = effectiveValue
  })

  return {
    effectiveProps
  }
}
